import {
  ALL_CLIENTS_OPTION,
  ALL_DEPARTMENTS_OPTION,
  ORDER_STATUS_OPTIONS,
} from '@/renderer/lib/utils';
import { Button, DropdownSelect } from '@components/common';
import type { DropdownSelectOption } from '@components/common/DropdownSelect';
import type { ApiResponse } from '@shared/types';
import { useAppSelector } from '@hooks/useRedux';
import { selectCurrentUser } from '@store/auth/authSelectors';
import { selectEffectiveOutlet, selectEffectiveOutletId } from '@store/outlets';
import { usePaymentMethods } from '@/renderer/hooks/accounting/usePaymentMethods';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchCompanyClients } from '../../../services/orderService';
import {
  fetchCreditorsReportRows,
  fetchDailySalesReportRows,
  fetchDepartmentSalesReportRows,
  fetchDiscountsReportRows,
  fetchItemReportDepartmentOptions,
  fetchItemsReportRows,
  fetchOrdersReportRows,
  fetchOrdersReportSummary,
  fetchPaymentMethodDailySummaryRows,
  fetchSettlementsReportRows,
} from '../../../services/reportsService';
import {
  exportSalesReportPdf,
  fetchScopedSalesReport,
  sendSalesReportEmail,
} from '../../../services/salesReportService';
import type {
  DepartmentSalesSummary,
  ItemSalesSummaryView,
  OrderSettlementView,
  OutletSalesReport,
  PaymentMethodDailySummary,
  SalesReportCreditorView,
  SalesReportDiscountView,
  SalesReportOrderView,
  SalesReportSummary,
} from '../../../types/report';
import type {
  OrderDetailsTriggerProps,
  ReportColumn,
  ReportExportColumn,
  ReportFilterOption,
  ReportMetric,
  ReportQueryParams,
} from '../../../types/reports';
import { apiError, apiInfo, apiSuccess } from '../../../util/notify';
import {
  formatReportDate,
  formatReportDateTime,
  formatReportMode,
  formatReportMoney,
  formatReportUserName,
} from '../../../util/reporting';
import { SalesReportModal } from '../../sales/SalesReportModal';
import { ItemSalesDetailsModal } from './ItemSalesDetailsModal';
import { OrderDetailsModal } from './OrderDetailsModal';
import { ReportRecordsPage } from './ReportRecordsPage';

const PAYMENT_METHOD_COLUMN_ORDER = [
  'CANCELLED',
  'CASH',
  'CHECK',
  'CARD',
  'CREDIT',
  'EFT',
  'MOBILE MONEY',
  'NC',
  'PENDING',
] as const;

const buildOrderLabel = (row: SalesReportOrderView): string => {
  if (row.table_name) return `${row.table_name} #${row.bill_number}`;
  if (row.client_name) return `${row.client_name} #${row.bill_number}`;
  return `${formatReportMode(row.mode)} #${row.bill_number}`;
};

const buildSettlementLabel = (row: OrderSettlementView): string => {
  if (row.table_name) return `${row.table_name} #${row.bill_number}`;
  if (row.client_name) return `${row.client_name} #${row.bill_number}`;
  return `${formatReportMode(row.mode)} #${row.bill_number}`;
};

const buildDiscountLabel = (row: SalesReportDiscountView): string => {
  if (row.table_name) return `${row.table_name} #${row.bill_number}`;
  if (row.client_name) return `${row.client_name} #${row.bill_number}`;
  return `${formatReportMode(row.mode)} #${row.bill_number}`;
};

const buildCreditorLabel = (row: SalesReportCreditorView): string => {
  if (row.table_name) return `${row.table_name} #${row.bill_number}`;
  if (row.client_name) return `${row.client_name} #${row.bill_number}`;
  return `${formatReportMode(row.mode)} #${row.bill_number}`;
};

const OrderDetailsTrigger: React.FC<OrderDetailsTriggerProps> = ({
  label,
  orderId,
  onOpen,
}) => (
  <button
    type="button"
    onClick={() => onOpen(orderId)}
    className="text-left font-semibold text-emerald-700 transition hover:text-emerald-800 hover:underline"
  >
    {label}
  </button>
);

const renderStatusBadge = (value: string) => {
  const normalized = value.toLowerCase();
  let tone = 'bg-slate-100 text-slate-700';

  if (normalized.includes('settled')) tone = 'bg-emerald-50 text-emerald-700';
  else if (normalized.includes('pending') || normalized.includes('open')) {
    tone = 'bg-amber-50 text-amber-700';
  } else if (normalized.includes('cancel')) {
    tone = 'bg-rose-50 text-rose-700';
  }

  return (
    <span
      className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${tone}`}
    >
      {value}
    </span>
  );
};

// Period-wide totals from the server — the table is paginated, so counting
// the loaded rows would only describe the current page.
const fetchOrdersMetrics = async (
  params: ReportQueryParams,
): Promise<ApiResponse<{ metrics: ReportMetric[]; totalCount: number }>> => {
  const response = await fetchOrdersReportSummary(params);
  if (!response.success || !response.data) {
    return { success: false, error: response.error };
  }

  const summary = response.data;
  return {
    success: true,
    data: {
      totalCount: summary.orderCount,
      metrics: [
        {
          label: 'Orders',
          value: summary.orderCount.toLocaleString(),
          hint: 'Orders in the selected period',
          tone: 'emerald',
        },
        {
          label: 'Pending items',
          value: summary.pendingItemCount.toLocaleString(),
          hint: 'Items still awaiting confirmation',
          tone: 'amber',
        },
        {
          label: 'Fully settled',
          value: summary.fullySettledCount.toLocaleString(),
          hint: 'Orders cleared in full',
          tone: 'blue',
        },
        {
          label: 'Net',
          value: formatReportMoney(summary.netAmount),
          hint: 'Combined order net amount',
          tone: 'slate',
        },
      ],
    },
  };
};

const buildSettlementsMetrics = (
  rows: OrderSettlementView[],
): ReportMetric[] => {
  const totalAmount = rows.reduce((sum, row) => sum + row.amount, 0);
  const methods = new Set(rows.map((row) => row.payment_method_name)).size;
  const bills = new Set(rows.map((row) => row.order_id)).size;

  return [
    {
      label: 'Settlements',
      value: rows.length.toLocaleString(),
      hint: 'Settlement entries in range',
      tone: 'emerald',
    },
    {
      label: 'Bills covered',
      value: bills.toLocaleString(),
      hint: 'Unique orders with settlements',
      tone: 'blue',
    },
    {
      label: 'Payment methods',
      value: methods.toLocaleString(),
      hint: 'Distinct methods represented',
      tone: 'amber',
    },
    {
      label: 'Settlement total',
      value: formatReportMoney(totalAmount),
      hint: 'Sum of all settlement entries',
      tone: 'slate',
    },
  ];
};

const buildDailySalesMetrics = (rows: SalesReportSummary[]): ReportMetric[] => {
  const gross = rows.reduce((sum, row) => sum + row.gross_sale, 0);
  const discounts = rows.reduce((sum, row) => sum + row.total_discount, 0);
  const net = rows.reduce((sum, row) => sum + row.total_sale, 0);
  const bills = rows.reduce((sum, row) => sum + row.settled_order_count, 0);

  return [
    {
      label: 'Business days',
      value: rows.length.toLocaleString(),
      hint: 'Records returned',
      tone: 'emerald',
    },
    {
      label: 'Bills settled',
      value: bills.toLocaleString(),
      hint: 'Orders settled across the range',
      tone: 'blue',
    },
    {
      label: 'Discounts',
      value: formatReportMoney(discounts),
      hint: 'Discount value granted',
      tone: 'amber',
    },
    {
      label: 'Net sale',
      value: formatReportMoney(net || gross),
      hint: 'Total sale after discounts',
      tone: 'slate',
    },
  ];
};

const buildPaymentMetrics = (
  rows: PaymentMethodDailySummary[],
): ReportMetric[] => {
  const totalAmount = rows.reduce((sum, row) => sum + row.total_amount, 0);
  const totalBills = rows.reduce(
    (sum, row) => sum + row.settled_order_count,
    0,
  );
  const totalSettlements = rows.reduce(
    (sum, row) => sum + row.settlement_count,
    0,
  );

  return [
    {
      label: 'Business days',
      value: rows.length.toLocaleString(),
      hint: 'Days returned in the current range',
      tone: 'emerald',
    },
    {
      label: 'Bills settled',
      value: totalBills.toLocaleString(),
      hint: 'Orders covered across all settlement types',
      tone: 'blue',
    },
    {
      label: 'Settlement count',
      value: totalSettlements.toLocaleString(),
      hint: 'Settlement entries recorded across the range',
      tone: 'amber',
    },
    {
      label: 'Collected total',
      value: formatReportMoney(totalAmount),
      hint: 'Combined total for all visible settlement types',
      tone: 'slate',
    },
  ];
};

const createPaymentSummaryColumns = (
  onOpen: (saleDate: string) => void,
): ReportColumn<PaymentMethodDailySummary>[] => [
  {
    key: 'sale_date',
    label: 'Date',
    className: 'min-w-[9rem]',
    render: (row) => formatReportDate(row.sale_date),
  },
  ...PAYMENT_METHOD_COLUMN_ORDER.map<ReportColumn<PaymentMethodDailySummary>>(
    (methodName) => ({
      key: methodName,
      label: methodName,
      align: 'right',
      className: 'min-w-[7rem]',
      render: (row) => formatReportMoney(row.method_totals[methodName] || 0),
    }),
  ),
  {
    key: 'total_amount',
    label: 'Total sales',
    align: 'right',
    className: 'min-w-[7rem] font-semibold',
    render: (row) => formatReportMoney(row.total_amount),
  },
  {
    key: 'report',
    label: 'Report',
    align: 'center',
    className: 'min-w-[9rem]',
    render: (row) => (
      <div className="flex flex-col gap-2 items-center">
        <Button
          type="button"
          size="xs"
          variant="text"
          onClick={() => onOpen(row.sale_date)}
          className="rounded-lg border border-emerald-200 bg-white px-3 text-xs font-semibold uppercase tracking-[0.14em] text-emerald-700 hover:bg-emerald-50 hover:text-emerald-900"
        >
          View
        </Button>
      </div>
    ),
  },
];

const buildDepartmentMetrics = (
  rows: DepartmentSalesSummary[],
): ReportMetric[] => {
  const net = rows.reduce((sum, row) => sum + row.total_sale, 0);
  const quantity = rows.reduce((sum, row) => sum + row.quantity_sold, 0);
  const itemCount = rows.reduce((sum, row) => sum + row.item_count, 0);
  const departments = new Set(rows.map((row) => row.department_name)).size;

  return [
    {
      label: 'Departments',
      value: departments.toLocaleString(),
      hint: 'Departments represented in range',
      tone: 'emerald',
    },
    {
      label: 'Item entries',
      value: itemCount.toLocaleString(),
      hint: 'Distinct item lines sold',
      tone: 'blue',
    },
    {
      label: 'Quantity sold',
      value: quantity.toLocaleString(),
      hint: 'Total units sold',
      tone: 'amber',
    },
    {
      label: 'Department sale',
      value: formatReportMoney(net),
      hint: 'Net sale by department view',
      tone: 'slate',
    },
  ];
};

const buildItemsMetrics = (rows: ItemSalesSummaryView[]): ReportMetric[] => {
  const quantity = rows.reduce((sum, row) => sum + row.quantity_sold, 0);
  const gross = rows.reduce((sum, row) => sum + row.gross_amount, 0);
  const uniqueItems = new Set(rows.map((row) => row.menu_item_id)).size;
  const orderCount = rows.reduce((sum, row) => sum + row.order_count, 0);

  return [
    {
      label: 'Items sold',
      value: uniqueItems.toLocaleString(),
      hint: 'Distinct items represented',
      tone: 'emerald',
    },
    {
      label: 'Quantity sold',
      value: quantity.toLocaleString(),
      hint: 'Total units sold',
      tone: 'blue',
    },
    {
      label: 'Orders covered',
      value: orderCount.toLocaleString(),
      hint: 'Orders contributing to the visible rows',
      tone: 'amber',
    },
    {
      label: 'Gross amount',
      value: formatReportMoney(gross),
      hint: 'Total gross item sales in range',
      tone: 'slate',
    },
  ];
};

const buildDiscountMetrics = (
  rows: SalesReportDiscountView[],
): ReportMetric[] => {
  const totalAmount = rows.reduce((sum, row) => sum + row.amount, 0);
  const bills = new Set(rows.map((row) => row.order_id)).size;
  const reasons = rows.filter((row) => Boolean(row.notes?.trim())).length;

  return [
    {
      label: 'Discount entries',
      value: rows.length.toLocaleString(),
      hint: 'Discount records in range',
      tone: 'emerald',
    },
    {
      label: 'Discounted bills',
      value: bills.toLocaleString(),
      hint: 'Unique orders with discounts',
      tone: 'blue',
    },
    {
      label: 'Reasons captured',
      value: reasons.toLocaleString(),
      hint: 'Discount records with notes',
      tone: 'amber',
    },
    {
      label: 'Discount total',
      value: formatReportMoney(totalAmount),
      hint: 'Total value discounted',
      tone: 'slate',
    },
  ];
};

const buildCreditorsMetrics = (
  rows: SalesReportCreditorView[],
): ReportMetric[] => {
  const totalAmount = rows.reduce((sum, row) => sum + row.amount, 0);
  const creditCount = rows.filter(
    (row) => row.payment_method_name.toLowerCase() === 'credit',
  ).length;
  const ncCount = rows.filter(
    (row) => row.payment_method_name.toLowerCase() === 'nc',
  ).length;
  const clientCount = new Set(rows.map((row) => row.client_id).filter(Boolean))
    .size;

  return [
    {
      label: 'Credit bills',
      value: creditCount.toLocaleString(),
      hint: 'Bills settled to credit',
      tone: 'emerald',
    },
    {
      label: 'NC bills',
      value: ncCount.toLocaleString(),
      hint: 'Non-chargeable settlements',
      tone: 'rose',
    },
    {
      label: 'Clients',
      value: clientCount.toLocaleString(),
      hint: 'Clients attached to credit activity',
      tone: 'blue',
    },
    {
      label: 'Outstanding issued',
      value: formatReportMoney(totalAmount),
      hint: 'Credit and NC total value',
      tone: 'slate',
    },
  ];
};

const createOrderColumns = (
  onOpen: (orderId: string) => void,
): ReportColumn<SalesReportOrderView>[] => [
  {
    key: 'sale_date',
    label: 'Date',
    render: (row) => formatReportDate(row.sale_date),
  },
  {
    key: 'bill_number',
    label: 'Bill',
    render: (row) => `#${row.bill_number}`,
  },
  {
    key: 'outlet_name',
    label: 'Outlet',
    render: (row) => row.outlet_name,
  },
  {
    key: 'order_label',
    label: 'Order',
    className: 'min-w-[12rem]',
    render: (row) => (
      <OrderDetailsTrigger
        label={buildOrderLabel(row)}
        orderId={row.order_id}
        onOpen={onOpen}
      />
    ),
  },
  {
    key: 'status',
    label: 'Status',
    render: (row) => renderStatusBadge(row.status_name),
  },
  {
    key: 'items',
    label: 'Items',
    align: 'right',
    render: (row) => row.item_count.toLocaleString(),
  },
  {
    key: 'pending',
    label: 'Pending',
    align: 'right',
    render: (row) => row.pending_item_count.toLocaleString(),
  },
  {
    key: 'client',
    label: 'Client',
    className: 'min-w-[10rem]',
    render: (row) => row.client_name || '',
  },
  {
    key: 'waiter',
    label: 'Waiter',
    className: 'min-w-[10rem]',
    render: (row) => row.waiter_name || 'Not assigned',
  },
  {
    key: 'discount',
    label: 'Discount',
    align: 'right',
    render: (row) => formatReportMoney(row.discount_amount),
  },
  {
    key: 'net',
    label: 'Net',
    align: 'right',
    render: (row) => formatReportMoney(row.net_amount),
  },
];

const createSettlementColumns = (
  onOpen: (orderId: string) => void,
): ReportColumn<OrderSettlementView>[] => [
  {
    key: 'created_at',
    label: 'Settled at',
    render: (row) => formatReportDateTime(row.created_at),
  },
  {
    key: 'bill_number',
    label: 'Bill',
    render: (row) => `#${row.bill_number}`,
  },
  {
    key: 'outlet_name',
    label: 'Outlet',
    render: (row) => row.outlet_name,
  },
  {
    key: 'order',
    label: 'Order',
    className: 'min-w-[12rem]',
    render: (row) => (
      <OrderDetailsTrigger
        label={buildSettlementLabel(row)}
        orderId={row.order_id}
        onOpen={onOpen}
      />
    ),
  },
  {
    key: 'payment_method_name',
    label: 'Settlement',
    render: (row) => row.payment_method_name,
  },
  {
    key: 'user',
    label: 'User',
    render: (row) =>
      formatReportUserName(row.created_by.first_name, row.created_by.last_name),
  },
  {
    key: 'client',
    label: 'Client',
    className: 'min-w-[10rem]',
    render: (row) => row.client_name || '',
  },
  {
    key: 'waiter',
    label: 'Waiter',
    className: 'min-w-[10rem]',
    render: (row) => row.waiter_name || 'Not assigned',
  },
  {
    key: 'notes',
    label: 'Notes',
    className: 'min-w-[12rem]',
    render: (row) => row.notes || '—',
  },
  {
    key: 'amount',
    label: 'Amount',
    align: 'right',
    render: (row) => formatReportMoney(row.amount),
  },
];

const createDailySalesColumns = (
  onOpen: (row: SalesReportSummary) => void,
): ReportColumn<SalesReportSummary>[] => [
  {
    key: 'sale_date',
    label: 'Date',
    render: (row) => formatReportDate(row.sale_date),
  },
  {
    key: 'outlet_name',
    label: 'Outlet',
    render: (row) => row.outlet_name,
  },
  {
    key: 'bills',
    label: 'Bills settled',
    align: 'right',
    render: (row) => row.settled_order_count.toLocaleString(),
  },
  {
    key: 'settlements',
    label: 'Settlements',
    align: 'right',
    render: (row) => row.settlement_count.toLocaleString(),
  },
  {
    key: 'gross_sale',
    label: 'Gross sale',
    align: 'right',
    render: (row) => formatReportMoney(row.gross_sale),
  },
  {
    key: 'discount',
    label: 'Discount',
    align: 'right',
    render: (row) => formatReportMoney(row.total_discount),
  },
  {
    key: 'net',
    label: 'Net sale',
    align: 'right',
    render: (row) => formatReportMoney(row.total_sale),
  },
  {
    key: 'avg',
    label: 'Average bill',
    align: 'right',
    render: (row) => formatReportMoney(row.average_bill_amount),
  },
  {
    key: 'report',
    label: 'Report',
    align: 'center',
    className: 'min-w-[9rem]',
    render: (row) => (
      <Button
        type="button"
        size="xs"
        variant="outline"
        onClick={() => onOpen(row)}
        className="rounded-lg border border-emerald-200 bg-white px-3 text-xs font-semibold uppercase tracking-[0.14em] text-emerald-700 hover:bg-emerald-50 hover:text-emerald-900"
      >
        View
      </Button>
    ),
  },
];

const departmentColumns: ReportColumn<DepartmentSalesSummary>[] = [
  {
    key: 'sale_date',
    label: 'Date',
    render: (row) => formatReportDate(row.sale_date),
  },
  {
    key: 'outlet_name',
    label: 'Outlet',
    render: (row) => row.outlet_name,
  },
  {
    key: 'department_name',
    label: 'Department',
    render: (row) => row.department_name,
  },
  {
    key: 'item_count',
    label: 'Item lines',
    align: 'right',
    render: (row) => row.item_count.toLocaleString(),
  },
  {
    key: 'quantity_sold',
    label: 'Qty sold',
    align: 'right',
    render: (row) => row.quantity_sold.toLocaleString(),
  },
  {
    key: 'gross_sale',
    label: 'Gross sale',
    align: 'right',
    render: (row) => formatReportMoney(row.gross_sale),
  },
  {
    key: 'discount',
    label: 'Discount',
    align: 'right',
    render: (row) => formatReportMoney(row.total_discount),
  },
  {
    key: 'net',
    label: 'Net sale',
    align: 'right',
    render: (row) => formatReportMoney(row.total_sale),
  },
];

const createItemColumns = (
  onOpen: (row: ItemSalesSummaryView) => void,
): ReportColumn<ItemSalesSummaryView>[] => [
  {
    key: 'sale_date',
    label: 'Date',
    className: 'min-w-[9rem]',
    render: (row) => formatReportDate(row.sale_date),
  },
  {
    key: 'outlet_name',
    label: 'Outlet',
    className: 'min-w-[10rem]',
    render: (row) => row.outlet_name,
  },
  {
    key: 'item_name',
    label: 'Item name',
    className: 'min-w-[18rem]',
    render: (row) => (
      <div className="space-y-1">
        <p className="font-semibold text-slate-900">{row.item_name}</p>
        <p className="text-xs text-slate-500">
          {row.department_name}
          {row.category_name ? ` · ${row.category_name}` : ''}
        </p>
      </div>
    ),
  },
  {
    key: 'unit_price',
    label: 'Unit price',
    align: 'right',
    className: 'min-w-[8rem]',
    render: (row) => formatReportMoney(row.unit_price),
  },
  {
    key: 'quantity_sold',
    label: 'Quantity',
    align: 'right',
    className: 'min-w-[9rem]',
    render: (row) => row.quantity_sold.toLocaleString(),
  },
  {
    key: 'gross',
    label: 'Gross',
    align: 'right',
    className: 'min-w-[8rem]',
    render: (row) => formatReportMoney(row.gross_amount),
  },
  {
    key: 'details',
    label: 'Details',
    align: 'center',
    className: 'min-w-[8rem]',
    render: (row) => (
      <Button
        variant="outline"
        size="sm"
        className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-700 hover:bg-slate-50"
        onClick={() => onOpen(row)}
      >
        Details
      </Button>
    ),
  },
];

const createDiscountColumns = (
  onOpen: (orderId: string) => void,
): ReportColumn<SalesReportDiscountView>[] => [
  {
    key: 'created_at',
    label: 'Date',
    render: (row) => formatReportDateTime(row.created_at),
  },
  {
    key: 'bill_number',
    label: 'Bill',
    render: (row) => `#${row.bill_number}`,
  },
  {
    key: 'outlet_name',
    label: 'Outlet',
    render: (row) => row.outlet_name,
  },
  {
    key: 'order',
    label: 'Order',
    className: 'min-w-[12rem]',
    render: (row) => (
      <OrderDetailsTrigger
        label={buildDiscountLabel(row)}
        orderId={row.order_id}
        onOpen={onOpen}
      />
    ),
  },
  {
    key: 'client',
    label: 'Client',
    className: 'min-w-[10rem]',
    render: (row) => row.client_name || '',
  },
  {
    key: 'waiter',
    label: 'Waiter',
    className: 'min-w-[10rem]',
    render: (row) => row.waiter_name || 'Not assigned',
  },
  {
    key: 'reason',
    label: 'Reason',
    className: 'min-w-[14rem]',
    render: (row) => row.notes || 'No reason captured',
  },
  {
    key: 'added_by',
    label: 'Added by',
    render: (row) =>
      formatReportUserName(row.added_by.first_name, row.added_by.last_name),
  },
  {
    key: 'amount',
    label: 'Amount',
    align: 'right',
    render: (row) => formatReportMoney(row.amount),
  },
];

const createCreditorColumns = (
  onOpen: (orderId: string) => void,
): ReportColumn<SalesReportCreditorView>[] => [
  {
    key: 'created_at',
    label: 'Settled at',
    render: (row) => formatReportDateTime(row.created_at),
  },
  {
    key: 'bill_number',
    label: 'Bill',
    render: (row) => `#${row.bill_number}`,
  },
  {
    key: 'outlet_name',
    label: 'Outlet',
    render: (row) => row.outlet_name,
  },
  {
    key: 'order',
    label: 'Order',
    className: 'min-w-[12rem]',
    render: (row) => (
      <OrderDetailsTrigger
        label={buildCreditorLabel(row)}
        orderId={row.order_id}
        onOpen={onOpen}
      />
    ),
  },
  {
    key: 'payment_method_name',
    label: 'Settlement',
    render: (row) => row.payment_method_name,
  },
  {
    key: 'client_name',
    label: 'Client',
    className: 'min-w-[10rem]',
    render: (row) => row.client_name || '',
  },
  {
    key: 'contact',
    label: 'Contact',
    className: 'min-w-[12rem]',
    render: (row) => row.client_contact || row.client_email || '—',
  },
  {
    key: 'created_by',
    label: 'User',
    render: (row) =>
      formatReportUserName(row.created_by.first_name, row.created_by.last_name),
  },
  {
    key: 'notes',
    label: 'Notes',
    className: 'min-w-[12rem]',
    render: (row) => row.notes || '—',
  },
  {
    key: 'amount',
    label: 'Amount',
    align: 'right',
    render: (row) => formatReportMoney(row.amount),
  },
];

const orderExportColumns: ReportExportColumn<SalesReportOrderView>[] = [
  {
    key: 'date',
    header: 'Date',
    value: (row) => formatReportDate(row.sale_date),
  },
  { key: 'bill', header: 'Bill', value: (row) => `#${row.bill_number}` },
  { key: 'outlet', header: 'Outlet', value: (row) => row.outlet_name },
  { key: 'order', header: 'Order', value: (row) => buildOrderLabel(row) },
  { key: 'status', header: 'Status', value: (row) => row.status_name },
  { key: 'items', header: 'Items', value: (row) => row.item_count },
  { key: 'pending', header: 'Pending', value: (row) => row.pending_item_count },
  {
    key: 'client',
    header: 'Client',
    value: (row) => row.client_name || '',
  },
  {
    key: 'waiter',
    header: 'Waiter',
    value: (row) => row.waiter_name || 'Not assigned',
  },
  {
    key: 'discount',
    header: 'Discount',
    value: (row) => formatReportMoney(row.discount_amount),
  },
  {
    key: 'net_amount',
    header: 'Net',
    value: (row) => formatReportMoney(row.net_amount),
  },
];

const settlementExportColumns: ReportExportColumn<OrderSettlementView>[] = [
  {
    key: 'settled_at',
    header: 'Settled at',
    value: (row) => formatReportDateTime(row.created_at),
  },
  { key: 'bill', header: 'Bill', value: (row) => `#${row.bill_number}` },
  { key: 'outlet', header: 'Outlet', value: (row) => row.outlet_name },
  { key: 'order', header: 'Order', value: (row) => buildSettlementLabel(row) },
  {
    key: 'settlement',
    header: 'Settlement',
    value: (row) => row.payment_method_name,
  },
  {
    key: 'user',
    header: 'User',
    value: (row) =>
      formatReportUserName(row.created_by.first_name, row.created_by.last_name),
  },
  {
    key: 'client',
    header: 'Client',
    value: (row) => row.client_name || '',
  },
  {
    key: 'waiter',
    header: 'Waiter',
    value: (row) => row.waiter_name || 'Not assigned',
  },
  { key: 'notes', header: 'Notes', value: (row) => row.notes || '—' },
  {
    key: 'amount',
    header: 'Amount',
    value: (row) => formatReportMoney(row.amount),
  },
];

const dailySalesExportColumns: ReportExportColumn<SalesReportSummary>[] = [
  {
    key: 'date',
    header: 'Date',
    value: (row) => formatReportDate(row.sale_date),
  },
  { key: 'outlet', header: 'Outlet', value: (row) => row.outlet_name },
  {
    key: 'bills_settled',
    header: 'Bills settled',
    value: (row) => row.settled_order_count,
  },
  {
    key: 'settlements',
    header: 'Settlements',
    value: (row) => row.settlement_count,
  },
  {
    key: 'gross_sale',
    header: 'Gross sale',
    value: (row) => formatReportMoney(row.gross_sale),
  },
  {
    key: 'discount',
    header: 'Discount',
    value: (row) => formatReportMoney(row.total_discount),
  },
  {
    key: 'net_sale',
    header: 'Net sale',
    value: (row) => formatReportMoney(row.total_sale),
  },
  {
    key: 'average_bill',
    header: 'Average bill',
    value: (row) => formatReportMoney(row.average_bill_amount),
  },
];

const paymentExportColumns: ReportExportColumn<PaymentMethodDailySummary>[] = [
  {
    key: 'date',
    header: 'Date',
    value: (row) => formatReportDate(row.sale_date),
  },
  ...PAYMENT_METHOD_COLUMN_ORDER.map<
    ReportExportColumn<PaymentMethodDailySummary>
  >((methodName) => ({
    key: methodName.toLowerCase().replace(/\s+/g, '_'),
    header: methodName,
    value: (row) => formatReportMoney(row.method_totals[methodName] || 0),
  })),
  {
    key: 'total_amount',
    header: 'Total sales',
    value: (row) => formatReportMoney(row.total_amount),
  },
];

const departmentExportColumns: ReportExportColumn<DepartmentSalesSummary>[] = [
  {
    key: 'date',
    header: 'Date',
    value: (row) => formatReportDate(row.sale_date),
  },
  { key: 'outlet', header: 'Outlet', value: (row) => row.outlet_name },
  {
    key: 'department',
    header: 'Department',
    value: (row) => row.department_name,
  },
  { key: 'item_lines', header: 'Item lines', value: (row) => row.item_count },
  {
    key: 'quantity_sold',
    header: 'Qty sold',
    value: (row) => row.quantity_sold,
  },
  {
    key: 'gross_sale',
    header: 'Gross sale',
    value: (row) => formatReportMoney(row.gross_sale),
  },
  {
    key: 'discount',
    header: 'Discount',
    value: (row) => formatReportMoney(row.total_discount),
  },
  {
    key: 'net_sale',
    header: 'Net sale',
    value: (row) => formatReportMoney(row.total_sale),
  },
];

const itemExportColumns: ReportExportColumn<ItemSalesSummaryView>[] = [
  {
    key: 'date',
    header: 'Date',
    value: (row) => formatReportDate(row.sale_date),
  },
  { key: 'outlet', header: 'Outlet', value: (row) => row.outlet_name },
  { key: 'item', header: 'Item', value: (row) => row.item_name },
  {
    key: 'department',
    header: 'Department',
    value: (row) => row.department_name,
  },
  {
    key: 'category',
    header: 'Category',
    value: (row) => row.category_name,
  },
  {
    key: 'quantity',
    header: 'Quantity sold',
    value: (row) => row.quantity_sold,
  },
  {
    key: 'unit_price',
    header: 'Unit price',
    value: (row) => formatReportMoney(row.unit_price),
  },
  {
    key: 'gross',
    header: 'Gross',
    value: (row) => formatReportMoney(row.gross_amount),
  },
  { key: 'orders', header: 'Orders covered', value: (row) => row.order_count },
];

const discountExportColumns: ReportExportColumn<SalesReportDiscountView>[] = [
  {
    key: 'added_at',
    header: 'Date',
    value: (row) => formatReportDateTime(row.created_at),
  },
  { key: 'bill', header: 'Bill', value: (row) => `#${row.bill_number}` },
  { key: 'outlet', header: 'Outlet', value: (row) => row.outlet_name },
  { key: 'order', header: 'Order', value: (row) => buildDiscountLabel(row) },
  {
    key: 'client',
    header: 'Client',
    value: (row) => row.client_name || '',
  },
  {
    key: 'waiter',
    header: 'Waiter',
    value: (row) => row.waiter_name || 'Not assigned',
  },
  {
    key: 'reason',
    header: 'Reason',
    value: (row) => row.notes || 'No reason captured',
  },
  {
    key: 'added_by',
    header: 'Added by',
    value: (row) =>
      formatReportUserName(row.added_by.first_name, row.added_by.last_name),
  },
  {
    key: 'amount',
    header: 'Amount',
    value: (row) => formatReportMoney(row.amount),
  },
];

const creditorExportColumns: ReportExportColumn<SalesReportCreditorView>[] = [
  {
    key: 'settled_at',
    header: 'Settled at',
    value: (row) => formatReportDateTime(row.created_at),
  },
  { key: 'bill', header: 'Bill', value: (row) => `#${row.bill_number}` },
  { key: 'outlet', header: 'Outlet', value: (row) => row.outlet_name },
  { key: 'order', header: 'Order', value: (row) => buildCreditorLabel(row) },
  {
    key: 'settlement',
    header: 'Settlement',
    value: (row) => row.payment_method_name,
  },
  {
    key: 'client',
    header: 'Client',
    value: (row) => row.client_name || '',
  },
  {
    key: 'contact',
    header: 'Contact',
    value: (row) => row.client_contact || row.client_email || '—',
  },
  {
    key: 'user',
    header: 'User',
    value: (row) =>
      formatReportUserName(row.created_by.first_name, row.created_by.last_name),
  },
  { key: 'notes', header: 'Notes', value: (row) => row.notes || '—' },
  {
    key: 'amount',
    header: 'Amount',
    value: (row) => formatReportMoney(row.amount),
  },
];

const CLIENT_PAGE_SIZE = 20;

export const OrdersReportPage: React.FC = () => {
  const currentUser = useAppSelector(selectCurrentUser);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [selectedStatusId, setSelectedStatusId] = useState('');

  const [clientOptions, setClientOptions] = useState<DropdownSelectOption[]>(
    [],
  );
  const [clientSearch, setClientSearch] = useState('');
  const [clientOffset, setClientOffset] = useState(0);
  const [hasMoreClients, setHasMoreClients] = useState(false);
  const [isLoadingClients, setIsLoadingClients] = useState(false);

  const loadClientsPage = useCallback(
    async (search: string, offset: number, append: boolean) => {
      if (!currentUser?.company?.id) return;
      setIsLoadingClients(true);
      const res = await fetchCompanyClients({
        search,
        offset,
        limit: CLIENT_PAGE_SIZE + 1,
      });
      setIsLoadingClients(false);
      if (!res.success) return;
      const items = res.data?.clients ?? [];
      const hasMore = items.length > CLIENT_PAGE_SIZE;
      const opts = items
        .slice(0, CLIENT_PAGE_SIZE)
        .map((c) => ({ value: c.id, label: c.full_name }));
      setHasMoreClients(hasMore);
      setClientOptions((prev) => (append ? [...prev, ...opts] : opts));
    },
    [currentUser?.company?.id],
  );

  useEffect(() => {
    setClientOffset(0);
    loadClientsPage(clientSearch, 0, false);
  }, [clientSearch, loadClientsPage]);

  const handleClientSearch = useCallback((search: string) => {
    setClientSearch(search);
  }, []);

  const handleLoadMoreClients = useCallback(() => {
    const next = clientOffset + CLIENT_PAGE_SIZE;
    setClientOffset(next);
    loadClientsPage(clientSearch, next, true);
  }, [clientOffset, clientSearch, loadClientsPage]);

  const buildOrderQueryParams = useCallback(
    ({ searchQuery: sq }: { searchQuery: string }) => ({
      search: sq.trim() || undefined,
      filters: {
        clientId: selectedClientId || undefined,
        statusId: selectedStatusId || undefined,
      },
    }),
    [selectedClientId, selectedStatusId],
  );

  const orderColumns = useMemo(
    () => createOrderColumns(setSelectedOrderId),
    [],
  );

  const ordersToolbar = (
    <div className="flex items-center gap-3">
      <div className="min-w-[14rem]">
        <DropdownSelect
          value={selectedClientId}
          options={[ALL_CLIENTS_OPTION, ...clientOptions]}
          placeholder="All clients"
          searchable
          searchPlaceholder="Search clients…"
          onSearchChange={handleClientSearch}
          hasMoreOptions={hasMoreClients}
          isLoadingOptions={isLoadingClients}
          onLoadMoreOptions={handleLoadMoreClients}
          onChange={setSelectedClientId}
        />
      </div>
      <div className="min-w-[11rem]">
        <DropdownSelect
          value={selectedStatusId}
          options={ORDER_STATUS_OPTIONS}
          placeholder="All statuses"
          searchable
          searchPlaceholder="Search statuses…"
          onChange={setSelectedStatusId}
        />
      </div>
    </div>
  );

  return (
    <>
      <ReportRecordsPage
        title="Orders report"
        description="Review day-by-day orders across the selected outlet or the whole company. Filter by client or settlement status to narrow results."
        searchPlaceholder="Search by bill, table, client, waiter, outlet or status"
        emptyTitle="No orders found"
        emptyDescription="There are no orders in this range for the current scope. Adjust the dates or switch the outlet scope to load more history."
        fetchRows={fetchOrdersReportRows}
        columns={orderColumns}
        exportConfig={{
          fileNamePrefix: 'orders-report',
          columns: orderExportColumns,
        }}
        fetchMetrics={fetchOrdersMetrics}
        getRowKey={(row) => row.order_id}
        getSearchValue={(row) =>
          [
            row.bill_number,
            row.outlet_name,
            row.table_name,
            row.section_name,
            row.client_name,
            row.waiter_name,
            row.status_name,
            row.mode,
            row.sale_date,
          ]
            .filter(Boolean)
            .join(' ')
        }
        searchMode="server"
        buildQueryParams={buildOrderQueryParams}
        toolbarContent={ordersToolbar}
      />
      <OrderDetailsModal
        isOpen={Boolean(selectedOrderId)}
        orderId={selectedOrderId}
        onClose={() => setSelectedOrderId(null)}
      />
    </>
  );
};

export const SettlementsReportPage: React.FC = () => {
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);

  return (
    <>
      <ReportRecordsPage
        title="Settlements report"
        description="Inspect every settlement entry recorded against orders in the selected period. This is useful for tracing payment methods, cashier activity, notes captured at settlement, and total money collected."
        searchPlaceholder="Search by bill, outlet, payment method, user, client or notes"
        emptyTitle="No settlements found"
        emptyDescription="No settlement activity matches the current filters. Try expanding the date range or switching outlet scope."
        fetchRows={fetchSettlementsReportRows}
        columns={createSettlementColumns(setSelectedOrderId)}
        exportConfig={{
          fileNamePrefix: 'settlements-report',
          columns: settlementExportColumns,
        }}
        buildMetrics={buildSettlementsMetrics}
        getRowKey={(row) => row.id}
        getSearchValue={(row) =>
          [
            row.bill_number,
            row.outlet_name,
            row.table_name,
            row.section_name,
            row.payment_method_name,
            row.client_name,
            row.waiter_name,
            row.notes,
            formatReportUserName(
              row.created_by.first_name,
              row.created_by.last_name,
            ),
          ]
            .filter(Boolean)
            .join(' ')
        }
      />
      <OrderDetailsModal
        isOpen={Boolean(selectedOrderId)}
        orderId={selectedOrderId}
        onClose={() => setSelectedOrderId(null)}
      />
    </>
  );
};

export const DailySalesReportPage: React.FC = () => {
  const currentUser = useAppSelector(selectCurrentUser);

  const [selectedReportRow, setSelectedReportRow] =
    useState<SalesReportSummary | null>(null);
  const [salesReport, setSalesReport] = useState<OutletSalesReport | null>(
    null,
  );
  const [isLoadingSalesReport, setIsLoadingSalesReport] = useState(false);
  const [isExportingSalesReport, setIsExportingSalesReport] = useState(false);
  const [isSendingSalesReport, setIsSendingSalesReport] = useState(false);

  const handleCloseSalesReport = useCallback(() => {
    setSelectedReportRow(null);
    setSalesReport(null);
  }, []);

  const handleOpenSalesReport = useCallback(
    async (row: SalesReportSummary) => {
      if (!currentUser?.company?.id) {
        apiError('Company details are missing for the current user.');
        return;
      }

      setSelectedReportRow(row);
      setSalesReport(null);
      setIsLoadingSalesReport(true);

      const response = await fetchScopedSalesReport({
        companyId: currentUser.company.id,
        outletId: row.outlet_id,
        outletName: row.outlet_name,
        businessDate: row.sale_date,
      });

      setIsLoadingSalesReport(false);

      if (!response.success || !response.data) {
        setSelectedReportRow(null);
        apiError(response.error || 'Failed to load sales report.');
        return;
      }

      setSalesReport(response.data);
    },
    [currentUser?.company?.id],
  );

  const handleExportSalesReport = useCallback(async () => {
    if (!salesReport || !selectedReportRow) {
      apiError('Load the sales report before exporting it.');
      return;
    }

    setIsExportingSalesReport(true);

    const response = await exportSalesReportPdf({
      report: salesReport,
      outletName: selectedReportRow.outlet_name,
      businessDate: selectedReportRow.sale_date,
    });

    setIsExportingSalesReport(false);

    if (!response.success || !response.data) {
      if (response.error !== 'Export cancelled') {
        apiError(response.error || 'Failed to export sales report PDF.');
      }
      return;
    }

    apiSuccess('Sales report PDF exported.');
  }, [salesReport, selectedReportRow]);

  const handleSendSalesReportEmail = useCallback(async () => {
    if (!salesReport || !selectedReportRow || !currentUser?.company?.id) {
      apiError('Load the sales report before sending it.');
      return;
    }

    setIsSendingSalesReport(true);

    const response = await sendSalesReportEmail({
      outletId: selectedReportRow.outlet_id,
      companyId: currentUser.company.id,
      outletName: selectedReportRow.outlet_name,
      businessDate: selectedReportRow.sale_date,
    });

    setIsSendingSalesReport(false);

    if (!response.success || !response.data) {
      apiError(response.error || 'Failed to send sales report email.');
      return;
    }

    if (response.data.recipientCount > 0) {
      apiSuccess(
        `Report email queued for ${response.data.recipientCount} recipient(s)`,
      );
    } else {
      apiInfo('No active email configurations found for this report');
    }
  }, [salesReport, selectedReportRow, currentUser?.company?.id]);

  const dailySalesColumns = useMemo(
    () => createDailySalesColumns(handleOpenSalesReport),
    [handleOpenSalesReport],
  );

  return (
    <>
      <ReportRecordsPage
        title="Daily sales report"
        description="See the summarized sales position for each business day and outlet. This page gives you a compact view of bills settled, settlement activity, gross sales, discounts, and net sales."
        searchPlaceholder="Search by outlet name or business day"
        emptyTitle="No daily summaries found"
        emptyDescription="There are no records of sales for this date range and scope yet."
        fetchRows={fetchDailySalesReportRows}
        columns={dailySalesColumns}
        exportConfig={{
          fileNamePrefix: 'daily-sales-report',
          columns: dailySalesExportColumns,
        }}
        buildMetrics={buildDailySalesMetrics}
        getRowKey={(row) => `${row.outlet_id}-${row.sale_date}`}
        getSearchValue={(row) => [row.outlet_name, row.sale_date].join(' ')}
      />

      <SalesReportModal
        isOpen={Boolean(selectedReportRow)}
        report={salesReport}
        outletName={selectedReportRow?.outlet_name || 'Outlet'}
        businessDate={selectedReportRow?.sale_date || null}
        isLoading={isLoadingSalesReport}
        isExporting={isExportingSalesReport}
        isSendingEmail={isSendingSalesReport}
        onClose={handleCloseSalesReport}
        onExport={handleExportSalesReport}
        onSendEmail={handleSendSalesReportEmail}
      />
    </>
  );
};

export const PaymentMethodsReportPage: React.FC = () => {
  const currentUser = useAppSelector(selectCurrentUser);
  const selectedOutletId = useAppSelector(selectEffectiveOutletId);
  const selectedOutlet = useAppSelector(selectEffectiveOutlet);

  const [selectedReportDate, setSelectedReportDate] = useState<string | null>(
    null,
  );
  const [salesReport, setSalesReport] = useState<OutletSalesReport | null>(
    null,
  );
  const [isLoadingSalesReport, setIsLoadingSalesReport] = useState(false);
  const [isExportingSalesReport, setIsExportingSalesReport] = useState(false);
  const [isSendingSalesReport, setIsSendingSalesReport] = useState(false);

  const scopeLabel = selectedOutlet?.name || 'All outlets';

  const handleCloseSalesReport = useCallback(() => {
    setSelectedReportDate(null);
    setSalesReport(null);
  }, []);

  const handleOpenSalesReport = useCallback(
    async (saleDate: string) => {
      if (!currentUser?.company?.id) {
        apiError('Company details are missing for the current user.');
        return;
      }

      setSelectedReportDate(saleDate);
      setSalesReport(null);
      setIsLoadingSalesReport(true);

      const response = await fetchScopedSalesReport({
        companyId: currentUser.company.id,
        outletId: selectedOutletId,
        outletName: scopeLabel,
        businessDate: saleDate,
      });

      setIsLoadingSalesReport(false);

      if (!response.success || !response.data) {
        setSelectedReportDate(null);
        apiError(response.error || 'Failed to load sales report.');
        return;
      }

      setSalesReport(response.data);
    },
    [currentUser?.company?.id, scopeLabel, selectedOutletId],
  );

  const handleExportSalesReport = useCallback(async () => {
    if (!salesReport || !selectedReportDate) {
      apiError('Load the sales report before exporting it.');
      return;
    }

    setIsExportingSalesReport(true);

    const response = await exportSalesReportPdf({
      report: salesReport,
      outletName: scopeLabel,
      businessDate: selectedReportDate,
    });

    setIsExportingSalesReport(false);

    if (!response.success || !response.data) {
      if (response.error !== 'Export cancelled') {
        apiError(response.error || 'Failed to export sales report PDF.');
      }
      return;
    }

    apiSuccess('Sales report PDF exported.');
  }, [salesReport, scopeLabel, selectedReportDate]);

  const handleSendSalesReportEmail = useCallback(async () => {
    if (
      !salesReport ||
      !selectedOutlet ||
      !selectedReportDate ||
      !currentUser?.company?.id
    ) {
      apiError('Load the sales report before sending it.');
      return;
    }

    setIsSendingSalesReport(true);

    const response = await sendSalesReportEmail({
      outletId: selectedOutlet.id,
      companyId: currentUser.company.id,
      outletName: selectedOutlet.name,
      businessDate: selectedReportDate,
    });

    setIsSendingSalesReport(false);

    if (!response.success || !response.data) {
      apiError(response.error || 'Failed to send sales report email.');
      return;
    }

    if (response.data.recipientCount > 0) {
      apiSuccess(
        `Report email queued for ${response.data.recipientCount} recipient(s)`,
      );
    } else {
      apiInfo('No active email configurations found for this report');
    }
  }, [
    salesReport,
    selectedOutlet,
    selectedReportDate,
    currentUser?.company?.id,
  ]);

  const paymentSummaryColumns = useMemo(
    () => createPaymentSummaryColumns(handleOpenSalesReport),
    [handleOpenSalesReport],
  );

  return (
    <>
      <ReportRecordsPage
        title="Sales summary report"
        description="Review one business day per row with settlement totals split by payment method. This sales summary layout is designed for quick reconciliation of cash, card, credit, NC, cancelled, and pending collections."
        searchPlaceholder="Search by business day"
        emptyTitle="No sales summary data found"
        emptyDescription="There are no payment method summaries for the selected filters."
        fetchRows={fetchPaymentMethodDailySummaryRows}
        columns={paymentSummaryColumns}
        exportConfig={{
          fileNamePrefix: 'sales-summary-report',
          columns: paymentExportColumns,
        }}
        buildMetrics={buildPaymentMetrics}
        getRowKey={(row) => row.sale_date}
        getSearchValue={(row) => row.sale_date}
      />

      <SalesReportModal
        isOpen={Boolean(selectedReportDate)}
        report={salesReport}
        outletName={scopeLabel}
        businessDate={selectedReportDate}
        isLoading={isLoadingSalesReport}
        isExporting={isExportingSalesReport}
        isSendingEmail={isSendingSalesReport}
        onClose={handleCloseSalesReport}
        onExport={handleExportSalesReport}
        onSendEmail={handleSendSalesReportEmail}
      />
    </>
  );
};

export const DepartmentSalesReportPage: React.FC = () => (
  <ReportRecordsPage
    title="Department sales report"
    description="Review sales performance by department so owners can see how much each department contributes across the selected outlet or all outlets."
    searchPlaceholder="Search by department, outlet or business day"
    emptyTitle="No department sales found"
    emptyDescription="The department sales view returned no rows for the selected range."
    fetchRows={fetchDepartmentSalesReportRows}
    columns={departmentColumns}
    exportConfig={{
      fileNamePrefix: 'department-sales-report',
      columns: departmentExportColumns,
    }}
    buildMetrics={buildDepartmentMetrics}
    getRowKey={(row) =>
      `${row.outlet_id}-${row.sale_date}-${row.department_id || row.department_name}`
    }
    getSearchValue={(row) =>
      [row.sale_date, row.outlet_name, row.department_name].join(' ')
    }
  />
);

export const ItemsReportPage: React.FC = () => {
  const currentUser = useAppSelector(selectCurrentUser);
  const selectedOutletId = useAppSelector(selectEffectiveOutletId);
  const [departmentOptions, setDepartmentOptions] = useState<
    ReportFilterOption[]
  >([]);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState('');
  const [selectedSettlementTypeIds, setSelectedSettlementTypeIds] = useState<
    string[]
  >([]);
  const { data: paymentMethods = [] } = usePaymentMethods();
  const [selectedItemSummary, setSelectedItemSummary] =
    useState<ItemSalesSummaryView | null>(null);
  const handleOpenItemSummary = useCallback((row: ItemSalesSummaryView) => {
    setSelectedItemSummary(row);
  }, []);

  useEffect(() => {
    const companyId = currentUser?.company?.id;
    if (!companyId) {
      setDepartmentOptions([]);
      return undefined;
    }

    let isMounted = true;

    const loadDepartmentOptions = async () => {
      const response = await fetchItemReportDepartmentOptions(
        companyId,
        selectedOutletId,
      );

      if (!isMounted) return;

      if (!response.success || !response.data) {
        setDepartmentOptions([]);
        return;
      }

      setDepartmentOptions(response.data);
    };

    loadDepartmentOptions();

    return () => {
      isMounted = false;
    };
  }, [currentUser?.company?.id, selectedOutletId]);

  const buildItemQueryParams = useCallback(
    ({ searchQuery }: { searchQuery: string }) => ({
      search: searchQuery.trim() || undefined,
      filters: {
        departmentId: selectedDepartmentId || undefined,
        settlementTypeIds: selectedSettlementTypeIds.length
          ? selectedSettlementTypeIds
          : undefined,
      },
    }),
    [selectedDepartmentId, selectedSettlementTypeIds],
  );

  const itemColumns = useMemo(
    () => createItemColumns(handleOpenItemSummary),
    [handleOpenItemSummary],
  );

  const itemsFilters = (
    <div className="flex flex-wrap gap-3">
      <div className="min-w-[14rem]">
        <DropdownSelect
          value={selectedDepartmentId}
          options={[
            ALL_DEPARTMENTS_OPTION,
            ...departmentOptions.map((d) => ({ value: d.id, label: d.name })),
          ]}
          placeholder="All departments"
          searchable
          searchPlaceholder="Search departments…"
          onChange={setSelectedDepartmentId}
        />
      </div>
      <div className="min-w-[14rem]">
        <DropdownSelect
          values={selectedSettlementTypeIds}
          options={paymentMethods.map((method) => ({
            value: String(method.id),
            label: method.name,
          }))}
          placeholder="All settlement types"
          multiple
          searchable
          searchPlaceholder="Search settlement types…"
          onMultiChange={setSelectedSettlementTypeIds}
        />
      </div>
    </div>
  );

  return (
    <>
      <ReportRecordsPage
        title="Items report"
        description="Review sold items by business day so owners can quickly see what moved, how much was sold, and the gross value generated for the selected outlet scope."
        searchPlaceholder="Search a specific item, department, category or outlet"
        emptyTitle="No item sales found"
        emptyDescription="There are no sold-item summaries for the selected filters."
        fetchRows={fetchItemsReportRows}
        columns={itemColumns}
        exportConfig={{
          fileNamePrefix: 'items-report',
          columns: itemExportColumns,
        }}
        buildMetrics={buildItemsMetrics}
        getRowKey={(row) => row.id}
        getSearchValue={(row) =>
          [
            row.sale_date,
            row.item_name,
            row.department_name,
            row.category_name,
            row.outlet_name,
          ].join(' ')
        }
        searchMode="server"
        buildQueryParams={buildItemQueryParams}
        toolbarContent={itemsFilters}
      />

      <ItemSalesDetailsModal
        isOpen={Boolean(selectedItemSummary)}
        summary={selectedItemSummary}
        onClose={() => setSelectedItemSummary(null)}
      />
    </>
  );
};

export const DiscountsReportPage: React.FC = () => {
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);

  return (
    <>
      <ReportRecordsPage
        title="Discounts report"
        description="Audit every discount issued in the selected period. This page helps owners see who approved discounts, why they were given, and which bills they affected."
        searchPlaceholder="Search by bill, reason, user, client, waiter or outlet"
        emptyTitle="No discounts found"
        emptyDescription="There are no discount records in this range for the current outlet scope."
        fetchRows={fetchDiscountsReportRows}
        columns={createDiscountColumns(setSelectedOrderId)}
        exportConfig={{
          fileNamePrefix: 'discounts-report',
          columns: discountExportColumns,
        }}
        buildMetrics={buildDiscountMetrics}
        getRowKey={(row) => row.id}
        getSearchValue={(row) =>
          [
            row.bill_number,
            row.outlet_name,
            row.table_name,
            row.client_name,
            row.waiter_name,
            row.notes,
            formatReportUserName(
              row.added_by.first_name,
              row.added_by.last_name,
            ),
          ]
            .filter(Boolean)
            .join(' ')
        }
      />
      <OrderDetailsModal
        isOpen={Boolean(selectedOrderId)}
        orderId={selectedOrderId}
        onClose={() => setSelectedOrderId(null)}
      />
    </>
  );
};

export const CreditorsReportPage: React.FC = () => {
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);

  return (
    <>
      <ReportRecordsPage
        title="Creditors and NC report"
        description="Review bills settled as credit or NC so business owners can see who those settlements belong to, which user processed them, and the total value issued."
        searchPlaceholder="Search by bill, settlement type, client, user or outlet"
        emptyTitle="No credit or NC activity found"
        emptyDescription="There are no CREDIT or NC settlements matching the current filters."
        fetchRows={fetchCreditorsReportRows}
        columns={createCreditorColumns(setSelectedOrderId)}
        exportConfig={{
          fileNamePrefix: 'creditors-report',
          columns: creditorExportColumns,
        }}
        buildMetrics={buildCreditorsMetrics}
        getRowKey={(row) => row.id}
        getSearchValue={(row) =>
          [
            row.bill_number,
            row.outlet_name,
            row.payment_method_name,
            row.client_name,
            row.client_contact,
            row.client_email,
            row.notes,
            formatReportUserName(
              row.created_by.first_name,
              row.created_by.last_name,
            ),
          ]
            .filter(Boolean)
            .join(' ')
        }
      />
      <OrderDetailsModal
        isOpen={Boolean(selectedOrderId)}
        orderId={selectedOrderId}
        onClose={() => setSelectedOrderId(null)}
      />
    </>
  );
};
